import Styles from './Social.module.scss'
import { Section } from './Section'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { Google, LinkAngled } from 'blaze-ui/icons'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { SelectedBlogPlatform } from 'types'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import { Account } from 'screens/BrandKit/SourceMaterials/SourceCreationModal/Account'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { WordPressBrand } from 'blaze-ui/icons/35'
import { GoHighLevelBrand } from 'blaze-ui/icons/35/GoHighLevelBrand'
import { ConnectIntegrationTrigger } from 'components/ConnectIntegrationTrigger/ConnectIntegrationTrigger'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { IntegrationAccountRow } from 'components/FirstTimeExperienceModal/steps/BrandPlanCurrentVolume/IntegrationAccountRow'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const WordPressRow = ({ integration }: { integration: ExternalIntegration }) => {
  return (
    <div className={Styles.row}>
      <div className={Styles.rowTitle}>
        <WordPressBrand size={24} />
        <Text variant="largeList">WordPress</Text>
      </div>
      <Account source={integration} avatarSize={32} />
    </div>
  )
}

const GoHighLevelRow = ({ integration }: { integration: ExternalIntegration }) => {
  return (
    <div className={Styles.row}>
      <div className={Styles.rowTitle}>
        <GoHighLevelBrand size={24} />
        <Text variant="largeList">GoHighLevel</Text>
      </div>
      <Account source={integration} avatarSize={32} />
    </div>
  )
}

export const Seo = ({
  size = 'md',
  selectedBlogPlatform,
}: {
  size?: 'sm' | 'md'
  selectedBlogPlatform?: SelectedBlogPlatform
}) => {
  const { isMobile } = useResponsiveDesign()
  const { connect } = useConnectAccountPopup()
  const isGoogleIntegrationEnabled = useFlipperFlag('google_integration')
  const { data: integrations } = useExternalIntegrations()

  const analyticsIntegration = integrations?.external_integrations?.find(
    (integration) =>
      integration.provider === ExternalIntegrationEnum.Google && integration.account_type === 'analytics',
  )
  const wordPressIntegration = integrations?.external_integrations?.find(
    (integration) => integration.provider === ExternalIntegrationEnum.WordPress,
  )
  const goHighLevelIntegration = integrations?.external_integrations?.find(
    (integration) => integration.provider === ExternalIntegrationEnum.GoHighLevel,
  )

  const shouldShowGoHighLevel =
    selectedBlogPlatform === ExternalIntegrationEnum.GoHighLevel ||
    (!selectedBlogPlatform && (goHighLevelIntegration || !wordPressIntegration))
  const shouldShowWordPress =
    selectedBlogPlatform === ExternalIntegrationEnum.WordPress ||
    (!selectedBlogPlatform && (wordPressIntegration || !goHighLevelIntegration))

  if (selectedBlogPlatform === 'generic') {
    return null
  }

  return (
    <Section title="Blog" level={size === 'sm' ? 4 : 2}>
      <div className={Styles.container}>
        {shouldShowWordPress && wordPressIntegration && <WordPressRow integration={wordPressIntegration} />}
        {shouldShowWordPress && !wordPressIntegration && (
          <div className={Styles.row}>
            <div className={Styles.rowTitle}>
              <WordPressBrand size={24} />
              <Text variant={isMobile ? 'smallList' : 'largeList'}>WordPress</Text>
            </div>
            <ConnectIntegrationTrigger provider={ExternalIntegrationEnum.WordPress}>
              <Button
                variant="secondary"
                size={isMobile ? 'sm' : 'lg'}
                frontIcon={LinkAngled}
                aria-label="Connect WordPress"
              >
                {isMobile ? 'Connect' : 'Connect WordPress'}
              </Button>
            </ConnectIntegrationTrigger>
          </div>
        )}

        {shouldShowGoHighLevel && goHighLevelIntegration && <GoHighLevelRow integration={goHighLevelIntegration} />}
        {shouldShowGoHighLevel && !goHighLevelIntegration && (
          <div className={Styles.row}>
            <div className={Styles.rowTitle}>
              <GoHighLevelBrand size={24} />
              <Text variant={isMobile ? 'smallList' : 'largeList'}>GoHighLevel</Text>
            </div>
            <InitiateOauthTrigger provider={ExternalIntegrationEnum.GoHighLevel} popUp onInitiatedOauth={connect}>
              <Button
                variant="secondary"
                size={isMobile ? 'sm' : 'lg'}
                frontIcon={LinkAngled}
                aria-label="Connect GoHighLevel"
              >
                {isMobile ? 'Connect' : 'Connect GoHighLevel'}
              </Button>
            </InitiateOauthTrigger>
          </div>
        )}

        {analyticsIntegration && isGoogleIntegrationEnabled && (
          <IntegrationAccountRow integration={analyticsIntegration} icon={Google} name="Google Analytics" />
        )}
        {!analyticsIntegration && isGoogleIntegrationEnabled && (
          <div className={Styles.row}>
            <div className={Styles.rowTitle}>
              <Google size={24} />
              <Text variant={isMobile ? 'smallList' : 'largeList'}>Google Analytics</Text>
            </div>
            <InitiateOauthTrigger provider={ExternalIntegrationEnum.Google} popUp onInitiatedOauth={connect}>
              <Button
                variant="secondary"
                size={isMobile ? 'sm' : 'lg'}
                frontIcon={LinkAngled}
                aria-label="Connect Google Analytics"
              >
                {isMobile ? 'Connect' : 'Connect Google'}
              </Button>
            </InitiateOauthTrigger>
          </div>
        )}
      </div>
    </Section>
  )
}
